import { z } from "zod";

/**
 * Schema for rows written to the application_settings table. Kept minimal for
 * the foundation; business-specific settings schemas are added per-module later.
 */
export const applicationSettingSchema = z.object({
  key: z
    .string()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z0-9_.:-]+$/, "key may contain letters, digits, . _ : -"),
  value: z.unknown(),
  description: z.string().max(512).nullable().optional(),
});

export type ApplicationSettingInput = z.infer<typeof applicationSettingSchema>;

/** General application settings (Phase 5.9). */
export const generalSettingsSchema = z.object({
  applicationName: z.string().trim().min(1).max(120),
  theme: z.enum(["system", "light", "dark"]),
  autoRefreshEnabled: z.boolean().default(true),
  refreshIntervalSec: z.coerce.number().int().min(0).max(3600),
  timeZone: z.string().trim().max(64),
});

export type GeneralSettingsInput = z.infer<typeof generalSettingsSchema>;

const brandHex = z.string().regex(/^#[0-9a-f]{6}$/i, "Expected a six-digit hex color.");

/** Runtime-editable deployment identity stored in application_settings. */
export const deploymentBrandingSchema = z.object({
  primaryColor: brandHex,
  secondaryColor: brandHex,
  productName: z.string().trim().min(1).max(120),
  logoMark: z.string().trim().min(1).max(1_500_000),
});

export type DeploymentBrandingInput = z.infer<typeof deploymentBrandingSchema>;

// There is deliberately NO securitySettingsSchema / SecuritySettingsInput.
//
// The security block is read-only: every value in it (idle + absolute session
// timeouts, the length-first password policy, lockout thresholds, login
// rate-limit budgets, auth mode, MFA source) is enforced from env or code. The
// schema that used to live here accepted `sessionTimeoutMinutes` and password
// character-class toggles that NOTHING read — an editable control with no effect
// is worse than no control, so the write path is gone rather than deprecated.

// API-key schemas moved to ./api-key.js in the secure API-key rebuild. The old
// name-only create schema minted credentials with no scopes, no expiry and no
// verification path; it is intentionally gone rather than deprecated so nothing
// can keep calling it.
